import classnames from 'classnames'
import ResourceKeys from './ResourceKeys'

const EMPTY = {}

export default class ComponentTheme {
  
  /**
   * Create an ComponentTheme with { [ComponentName] : { classes, hierarchy, props, style }}
   **/
  constructor( sync, altTheme ) {
    this.component = sync ? sync.component || {} : {}
    this.theme = sync ? sync.theme || {} : {}
    this.rsrcs = this.theme.resources || { }
    this.altTheme = altTheme
    this.propCache = this.createPropCache()

    const k = Object.keys( this.rsrcs )
    for ( let n = 0; n < k.length; n++ ) {
      const c = this.rsrcs[k[n]]
      const nr = { }
      const ck = Object.keys( c )
      for ( let cn = 0; cn < ck.length; cn++ ) {
        const key = ck[cn]
        const nkey = ResourceKeys.fromFB( key )
        nr[nkey] = c[key]
      }
      this.rsrcs[k[n]] = nr
    }
  }

  resolveVariant( componentType, variant ) {
    const hier = this.component[componentType] && this.component[componentType].hierarchy
    if ( variant == null || hier == null ) {
      return null
    }

    const vs = variant.split( '|' )
    for ( let n = 0; n < vs.length; n++ ) {
      if ( hier[vs[n]] !== undefined ) {
        return vs[n]
      }
    }
    return null
  }

  isKnown( component ) {
    return this.component[component] != null
  }

  classes( component, type, variant, ...rest ) {

    const comp = this.component[component]
    if ( comp == null ) {
      return this.altTheme.classes( component, type, variant, ...rest )
    }
    const s = comp && comp.classes
    if ( s ) {
      // variant = variant == null ? null : s[`${type}-${variant}`]
      const rv = this.resolveVariant( component, variant )
      const variantCns = variantClassNames( comp, s, type, rv )
      const ud = usesDefault( comp, rv )
      const c = classnames( ud && s[type], variantCns, ...rest )
      return c
    }
    return classnames( rest )
  }

  subclasses( component, type, ...rest ) {

    let s = this.component[component]
    if ( s == null ) {
      return this.altTheme.subclasses( component, type, ...rest )
    }
    s = s && s.classes
    if ( s ) {
      const c = classnames( s[type], ...rest )
      return c
    }
    return classnames( rest )
  }

  /**
   * Returns any default props for the supplied Component and Variant.
   * If the third argument is an object, it will be overlaid on top of
   * the component's default props and the component's variant props.
   *
   * If the second argument is an object, it is considered to be the
   * React component's actual props object, and the variant is looked
   * up from it. This means from render, you can invoke:
   *   const { markClass } = theme.props( 'Checkbox', this.props )
   * @param {string} component the name of the component type. eg "Button"
   * @param {string|object} variant the variant name to requsest. eg "small"
   * @return {object} an object containing the default props. This will be a 
   * merger of the default variant's props and the specified variants props. 
   * This will always return an object
   **/
  props( component, variant, userProps = EMPTY ) {

    if ( variant != null && typeof variant === 'object' ) {
      return this.props( component, variant.variant, variant )
    }
    const s = this.component[component]
    if ( s == null ) {
      return this.altTheme.props( component, variant, userProps )
    }
    const props = this.propCache[component]// ( s && s.props ) || { }
    if ( props ) {
      const rv = this.resolveVariant( component, variant ) || 'default'
      const v = props[rv] || EMPTY // ( rv && props[rv] ) || props.default || EMPTY
      // Remove undefined props
      const up = Object.keys( userProps ).reduce( ( acc, key ) => {
        if ( userProps[key] !== undefined ) acc[key] = userProps[key]
        return acc
      }, {} )
      return { ...v, ...up }
    } 
    return userProps === EMPTY ? { } : userProps
  }
  
  resources( type, name ) {
    const rsrcs = this.rsrcs || EMPTY
    if ( name ) {
      return rsrcs?.[type]?.[name]
    }
    return rsrcs[type] || EMPTY
  }

  createPropCache() {
    const r = {}
    const s = this.component
    Object.keys( s ).forEach( ( ct ) => {
      r[ct] = { }
      const props = s[ct].props || {}
      const hier = s[ct].hierarchy || {}
      const hks = Object.keys( hier )
      hks.unshift( 'default' )
      hks.forEach( ( hk ) => {
        const isRoot = !usesDefault( s[ct], hk )
        const start = isRoot ? 1 : 0
        const h = hier[hk]
        let prp = isRoot ? {} : props.default || {}
        
        if ( h ) {
          for ( let n = start; n < h.length; n++ ) {
            // Strip <ct>- off of hk....consider more support objects
            const key = h[n].substring( h[n].indexOf( '-' ) + 1 )
            prp = { ...prp, ...props[key] }
          }
        }

        r[ct][hk] = prp
      } )
    } )
    return r
  }
}

/**
 * Turns the variant's hierarchy into a hashified set of classnames.
 * @param {object} component the redux model for the component type
 * @param {object} classes the jss classname lookup
 * @param {string} type the component type
 * @param {string} variant the component variant
 **/
function variantClassNames( component, classes, type, variant ) {

  if ( variant ) {
    if ( component.hierarchy && component.hierarchy[variant] ) {
      return component.hierarchy[variant].map( cn => classes[cn] )
    } 
    return classes[`${type}-${variant}`]
  }
  return null
}
function usesDefault( component, variant ) {
  return component?.hierarchy?.[variant]?.[0] !== '__root__'
}
